import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DateRangePicker, CustomProvider } from 'rsuite'
import ptBR from 'rsuite/locales/pt_BR'
const { afterToday } = DateRangePicker
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { Search } from '@mui/icons-material'
import {
  CustomTableCell,
  Filter,
  FilterDrawer,
  GroupedFilterTags,
  TableHead,
} from '../../components'
import useSystemEvents from '../../hooks/useSystemEvents'
import { getComparator } from '../../utils/graph'
import useViewport from '../../hooks/useViewport'
import filterDrawerHelper from '../../utils/filterDrawer'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f1f3f5;
`

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px 44px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: '100%';
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin: 16px 0;
`

const SearchContainer = styled.div`
  display: flex;
  flex: 2;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid #d6d7db;
  border-radius: 4px;
  padding: 4px 8px;
  height: 40px;
  gap: 8px;
`

const StyledInput = styled.input`
  border: 0;
  width: 100%;
  outline: none;
`

const StyledDateRangePicker = styled(DateRangePicker)`
  height: 20px;
`

const SystemEvents = () => {
  const { t } = useTranslation('common')
  const [page, setPage] = useState(0)
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const {
    loading: loadingData,
    rows,
    filterOptions,
    setSelectedFilterOptions,
    inputValue,
    handleType,
    dateRange,
    handleDateRange,
    selectedFilterOptions,
  } = useSystemEvents()
  const { width } = useViewport()
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper
  const headState = [
    {
      id: 'date',
      numeric: false,
      disablePadding: false,
      label: t('system_events.header.date'),
      align: 'left',
    },
    {
      id: 'employee',
      numeric: false,
      disablePadding: true,
      label: t('system_events.header.colaborator'),
      align: 'left',
    },
    {
      id: 'event',
      numeric: false,
      disablePadding: false,
      label: t('system_events.header.event'),
      align: 'left',
    },
    {
      id: 'eventType',
      numeric: false,
      disablePadding: false,
      label: t('system_events.header.event_type'),
      align: 'left',
    },
  ]

  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState(headState[0].id)
  const rowsPerPage = 10

  const actionTranslation = {
    create: t('system_events.actions.create'),
    update: t('system_events.actions.update'),
    acknowledged: t('system_events.actions.acknowledged'),
    warn: t('system_events.actions.warn'),
    completed: t('system_events.actions.completed'),
    delete: t('system_events.actions.deleted'),
  }

  const messageTranslation = {
    caffeine: t('system_events.messages.caffeine'),
    sleep: t('system_events.messages.sleep'),
    notification: t('system_events.messages.notification'),
    shift: t('system_events.messages.shift'),
    config: t('system_events.messages.config'),
    recommendation: t('system_events.messages.recommendation')
  }

  const translateEventType = value => {
    const translationKeys = {
      caffeine: t('system_events.keys.caffeine'),
      sleep: t('system_events.keys.sleep'),
      notification: t('system_events.keys.notification'),
      shift: t('system_events.keys.shift'),
      config: t('system_events.keys.config'),
      recommendation: t('system_events.keys.recommendation')
    }

    return translationKeys[value]
  }

  const createMessage = ({ action, message }) => {
    const translatedAction = actionTranslation[action?.toLowerCase()] || ''
    const translatedMessage = messageTranslation[message?.toLowerCase()] || ''

    if (!translatedAction && !translatedMessage) return '-'

    return `${translatedAction} ${translatedMessage}`
  }

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
  }

  const emptyRows = () => {
    return page > 0 || rows.length < 10
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0
  }

  const visibleRows = useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  )

  useEffect(() => {
    setPage(0)
  }, [selectedFilterOptions])

  return (
    <Container>
      <ContentContainer>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {t('system_events.title')}
            </span>
            <Tooltip
              title={t('system_events.tooltip')}
              placement='right'
              arrow
            >
              <HelpOutline fontSize='18' />
            </Tooltip>
          </div>
        </Header>
        <BodyContainer>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <FiltersContainer>
              <SearchContainer>
                <Search sx={{ color: '#858B92' }} />
                <StyledInput
                  placeholder={t('system_events.search_colaborator')}
                  value={inputValue}
                  onChange={handleType}
                />
              </SearchContainer>
              <div style={{ display: 'flex', gap: 8 }}>
                <CustomProvider locale={ptBR}>
                  <StyledDateRangePicker
                    defaultCalendarValue={dateRange}
                    value={dateRange}
                    onChange={value => handleDateRange({ value })}
                    showOneCalendar
                    shouldDisableDate={afterToday()}
                    character=' - '
                    placement='bottom'
                    placeholder={t('system_events.search_colaborator')}
                    ranges={[]}
                    cleanable={false}
                  />
                </CustomProvider>
                <Filter onClick={() => setOpenFilterDrawer(true)} />
              </div>
            </FiltersContainer>
            {selectedFilterOptionsList({
              selectedFilterOptions,
            }).length > 0 && (
                <GroupedFilterTags
                  tags={selectedFilterOptionsList({
                    selectedFilterOptions,
                  })}
                  onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                  maxTagsToShow={getMaxTagsToShow({ width })}
                />
              )}
          </div>
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby='systemEventsTable'
            >
              <TableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                headCells={headState}
              />
              <TableBody>
                {!loadingData ? (
                  <>
                    {rows.length > 0 ? (
                      <>
                        {visibleRows.map((row, index) => {
                          const labelId = `enhanced-table-checkbox-${index}`

                          return (
                            <TableRow
                              hover
                              tabIndex={-1}
                              key={row.id}
                            >
                              <CustomTableCell
                                data={row.date}
                                type='date'
                                width={180}
                              />
                              <CustomTableCell
                                component='td'
                                id={labelId}
                                scope='row'
                                padding='none'
                                data={row.employee}
                                width={120}
                              />
                              <CustomTableCell
                                data={createMessage({
                                  action: row.action,
                                  message: row.eventType,
                                })}
                              />
                              <CustomTableCell
                                data={translateEventType(row.eventType)}
                              />
                            </TableRow>
                          )
                        })}
                        {emptyRows() > 0 && (
                          <TableRow
                            style={{
                              height: 53 * emptyRows(),
                            }}
                          >
                            <TableCell colSpan={4} />
                          </TableRow>
                        )}
                      </>
                    ) : (
                      <TableRow style={{ height: 53 * 10 }}>
                        <TableCell
                          colSpan={4}
                          align='center'
                        >
                          {t('no_data')}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ) : (
                  <TableRow style={{ height: 53 * 10 }}>
                    <TableCell
                      colSpan={4}
                      align='center'
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            count={rows.length}
            showFirstButton
            showLastButton
            labelRowsPerPage={''}
            rowsPerPageOptions={[]}
            rowsPerPage={10}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ to, count }) =>
              {t('system_events.footer', {to: to, count: count})}
            }
          />
        </BodyContainer>
      </ContentContainer>
      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={setSelectedFilterOptions}
        loading={loadingData}
        options={filterOptions}
        withEventType
      />
    </Container>
  )
}

export default SystemEvents
